import { hasOwnProperty, prop } from '@gov-nx/core/types';
import { usePersistedStore } from '../constants';
import type { FiltersState } from './store.types';

const update =
	<T>(property: keyof T, value: object) =>
	(object: T): T => {
		const current = (object[property] as Record<string, unknown>) ?? {};
		const patch = value as Record<string, unknown>;
		const next: Record<string, unknown> = { ...current };

		for (const [key, patchValue] of Object.entries(patch)) {
			if (patchValue === undefined) {
				delete next[key];
				continue;
			}

			next[key] = patchValue;
		}

		return {
			...object,
			[property]: next as T[keyof T],
		};
	};

export const updateByPath =
	<T extends object, V>(path: string[], value: V) =>
	(obj: T): T => {
		const [first, ...rest] = path;

		const objectToUpdate =
			obj && hasOwnProperty(obj, first) ? (obj[first] as object) : {};

		const toUpdate =
			rest.length > 0 ? updateByPath(rest, value)(objectToUpdate) : value;

		return update(first as keyof T, toUpdate as object)(obj);
	};

const initialState = {
	filters: {},
};

const useStore = <T>(selector: (state: FiltersState) => T) =>
	usePersistedStore(
		(set) => ({
			...initialState,

			updateFiltersAction: (key, data) =>
				set(updateByPath(['filters', key], data)),

			updateFiltersWithPathAction: (keys, data) =>
				set(updateByPath(['filters', ...keys], data)),

			filtersResetAction: () => set(initialState),
		}),
		'filters',
		selector
	);

export const useFiltersStore = <T extends keyof FiltersState>(
	property: T
): FiltersState[T] => useStore(prop(property));

export const useFiltersStoreFilters = <T extends object>(
	key: string
): FiltersState<typeof key, T>['filters'][typeof key] =>
	useStore<T>((state) => state.filters[key] as T);
